<template>
  <div class="qz-fillblank" :class="{ 'qz-answered': isAnswered }">
    <div class="qz-stem">
      <span v-for="(seg, i) in segments" :key="i" class="qz-seg">
        <template v-if="seg.kind === 'text'">{{ seg.text }}</template>
        <span v-else class="qz-blank-wrap">
          <label v-if="seg.label" class="qz-blank-label">{{ seg.label }}</label>
          <input
            type="text"
            class="qz-blank-input"
            :class="blankStatusClass(seg.index)"
            :value="blanks[seg.index]"
            :aria-label="seg.label || `第${seg.index + 1}空`"
            :disabled="isAnswered"
            @input="handleInput(seg.index, $event.target.value)"
          />
          <span v-if="showBlankAnswer" class="qz-blank-answer" :class="blankStatusClass(seg.index)">
            {{ correctBlankText(seg.index) }}
          </span>
        </span>
      </span>
    </div>
    <div v-if="isAnswered" class="qz-feedback">
      <!-- eslint-disable vue/no-v-html -->
      <div v-if="analysisText" class="qz-analysis" v-html="analysisText"></div>
      <!-- eslint-enable vue/no-v-html -->
    </div>
  </div>
</template>

<script>
import { normalizeBlank } from '../utils'

// stem 取舍说明（契约 D7 补充裁定）：其余渲染器的 view.stem 走 v-html（服务端已 bluemonday
// 消毒），但 fill_blank 的 stem 需要按 "____" 切成多段、段间插入 <input>——v-html 整体注入会
// 把富文本节点结构和我们插入的 input 混在一起，无法可靠定位插空位置。因此本组件放弃 stem 的
// 富文本能力：先 strip 掉所有 HTML 标签（正则 <[^>]*>），再解码几个基础 HTML 实体
// （&lt; &gt; &quot; &#39; &apos; &nbsp; &amp;），最后按下划线切段、以纯文本插值（{{ }}，永远是安全文本节点，不解析
// 标签）渲染每一段。取舍：填空题干的富文本能力让位于"插槽位置正确"这个更硬的约束。
//
// 空位数量对齐规则（裁定）：渲染的 input 数量始终以 view.blanks（记为 N）为准，不以切段结果
// 为准：
//   - 切段找到的 "____" 数量 < N：差额在题干尾部补 input，并附"第 N 空"前缀标签（因为这些空
//     脱离了题干的行文位置，加标签避免学生分不清是第几空）。
//   - 切段找到的 "____" 数量 > N：只取前 N 个位置转成 input，多出来的 "____" 原样当文本显示
//     （不再消耗一个 blanks 下标）。
function stripTags (str) {
  return String(str || '').replace(/<[^>]*>/g, '')
}

function decodeEntities (str) {
  // 顺序：先处理具体实体，&amp; 放最后，避免把 "&amp;lt;" 这类双重编码错误地二次解码
  return String(str || '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
}

// 3-6 个连续下划线都视为一个空位标记（容忍学生/教研粘贴时长度不一致）
const BLANK_RE = /_{3,6}/g

export default {
  name: 'FillBlankRenderer',
  props: {
    view: {
      type: Object,
      required: true
    },
    session: {
      type: Object,
      default: null
    },
    env: {
      type: Object,
      required: true
    },
    feedback: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      blanks: this.restoreBlanks()
    }
  },
  computed: {
    isAnswered () {
      return !!this.feedback
    },
    blankCount () {
      return this.view.blanks || 0
    },
    strippedStem () {
      return decodeEntities(stripTags(this.view.stem))
    },
    // 把 strippedStem 切成 {kind:'text', text} / {kind:'blank', index, label} 的有序数组，
    // 始终恰好产出 blankCount 个 blank 段（对齐规则见上方脚本注释）
    segments () {
      const text = this.strippedStem
      const n = this.blankCount
      const result = []
      let lastIndex = 0
      let naturalCount = 0
      let match
      BLANK_RE.lastIndex = 0
      while ((match = BLANK_RE.exec(text)) !== null) {
        const before = text.slice(lastIndex, match.index)
        if (before) result.push({ kind: 'text', text: before })
        if (naturalCount < n) {
          result.push({ kind: 'blank', index: naturalCount, label: null })
        } else {
          result.push({ kind: 'text', text: match[0] })
        }
        naturalCount++
        lastIndex = BLANK_RE.lastIndex
      }
      const tail = text.slice(lastIndex)
      if (tail) result.push({ kind: 'text', text: tail })
      for (let i = naturalCount; i < n; i++) {
        result.push({ kind: 'blank', index: i, label: `第${i + 1}空` })
      }
      return result
    },
    showBlankAnswer () {
      return this.isAnswered && this.feedback.right !== 0
    },
    analysisText () {
      return this.feedback && this.feedback.analysis && this.feedback.analysis.text
    }
  },
  methods: {
    restoreBlanks () {
      const n = this.view.blanks || 0
      const saved = this.session && this.session.answer && this.session.answer.blanks
      const arr = new Array(n).fill('')
      if (Array.isArray(saved)) {
        for (let i = 0; i < n; i++) {
          if (typeof saved[i] === 'string') arr[i] = saved[i]
        }
      }
      return arr
    },
    handleInput (index, value) {
      if (this.isAnswered) return
      const next = [...this.blanks]
      next[index] = value
      this.blanks = next
      this.$emit('session-changed', {
        answer: { blanks: [...next] },
        complete: next.length > 0 && next.every(b => (b || '').trim() !== '')
      })
    },
    // correct_view.blanks[i] 是"可接受答案"数组（一空可能有多个正确写法）；防御非数组形状
    correctBlankList (index) {
      if (!this.isAnswered || this.feedback.right === 0) return []
      const blanksList = this.feedback.correct_view && this.feedback.correct_view.blanks
      const list = blanksList ? blanksList[index] : undefined
      if (Array.isArray(list)) return list
      return list !== undefined && list !== null ? [list] : []
    },
    correctBlankText (index) {
      return this.correctBlankList(index).join(' / ')
    },
    // 逐空对错色规则（评审裁定）：
    // - right=1：后端 gradeExactMulti 是 all-or-nothing，判对即全部空都对——前端信任该不变式
    //   无条件全绿（零归一化成本的安全网，避免本地比对偏差把"后端判对"的空标红）。
    // - right=2：逐空比对作展示辅助，归一化规则复刻后端 interaction_grader.go 的
    //   normalizeBlank（trim + 全角转半角 + toLowerCase，见 src/utils.js）——两端需同步演进。
    blankStatusClass (index) {
      if (!this.isAnswered || this.feedback.right === 0) return {}
      if (this.feedback.right === 1) return { 'qz-correct': true }
      const mine = normalizeBlank(this.blanks[index])
      const hit = this.correctBlankList(index).some(c => normalizeBlank(c) === mine)
      return {
        'qz-correct': hit,
        'qz-wrong': !hit
      }
    }
  }
}
</script>

<style src="../styles/base.css"></style>

<style scoped>
.qz-fillblank {
  position: relative;
}

.qz-seg {
  white-space: pre-wrap;
}

.qz-blank-wrap {
  display: inline-flex;
  align-items: center;
  margin: 0 4px;
  vertical-align: middle;
}

.qz-blank-label {
  font-size: 12px;
  color: var(--qz-text, #303133);
  opacity: 0.6;
  margin-right: 4px;
}

.qz-blank-input {
  min-width: 80px;
  min-height: var(--qz-input-height, 32px);
  padding: 5px 8px;
  border: 1px solid transparent;
  border-bottom: 2px solid #c0c4cc;
  border-radius: var(--qz-input-radius, 0);
  font-size: 15px;
  text-align: center;
  color: var(--qz-text, #303133);
  background: var(--qz-input-background, transparent);
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.qz-blank-input:focus {
  outline: none;
  border-color: var(--qz-primary, #409eff);
  border-bottom-color: var(--qz-primary, #409eff);
  box-shadow: 0 0 0 3px var(--qz-primary-focus-ring, rgba(64, 158, 255, 0.25));
}

.qz-fillblank.qz-answered .qz-blank-input {
  cursor: default;
}

.qz-blank-input.qz-correct,
.qz-blank-input.qz-wrong {
  border-bottom-width: 2px;
}

.qz-blank-answer {
  margin-left: 6px;
  font-size: 13px;
}

@media (prefers-reduced-motion: reduce) {
  .qz-blank-input {
    transition: none;
  }
}
</style>
